<template>
	<Drawer
		:title="$t('Lesson plan')"
		:initialWidth="initialWidth"
		:closeOnClickOutside="true"
		:skipOpenAnimation="skipOpenAnimation"
		@requestUnmount="hide"
	>
		<template #default="{ hide }">
			<div style="display: none">{{ setSlotHide(hide) }}</div>

			<div class="h-full px-6 pb-24 overflow-y-auto relative">

				<!-- header -->
				<div class="sticky top-0 z-20 bg-base/80 backdrop-blur-md pb-2 pt-4 mb-6">

					<slot name="topMessage"></slot>

					<div class="flex items-center">
						<div class="flex-1 min-w-0">
							<h1 class="w-full text-xl font-semibold">
								{{ lessonPlan.title }}
							</h1>
							<div v-if="showCreator" class="flex gap-2">
								<div class="flex items-center gap-2 text-xs text-accent-focus bg-accent-focus/10 py-0.5 px-2 mt-0.5 max-w-max rounded">
									<!-- Phosphor icon PenNib -->
									<svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="currentColor" viewBox="0 0 256 256">
										<path d="M248,92.68a15.86,15.86,0,0,0-4.69-11.31L174.63,12.68a16,16,0,0,0-22.63,0L123.57,41.11l-58,21.77A16.06,16.06,0,0,0,55.35,75.23L32.11,214.68A8,8,0,0,0,40,224a8.4,8.4,0,0,0,1.32-.11l139.44-23.24a16,16,0,0,0,12.35-10.17l21.77-58L243.31,104A15.87,15.87,0,0,0,248,92.68Zm-69.87,92.19L63.32,204l47.37-47.37a28,28,0,1,0-11.32-11.32L52,192.7,71.13,77.86,126,57.29,198.7,130ZM112,132a12,12,0,1,1,12,12A12,12,0,0,1,112,132Zm96-15.32L139.31,48l24-24L232,92.68Z"></path>
									</svg>
									<p>{{ lessonPlan.creatorName }}</p>
									<div v-if="lessonPlan.coCreatorName" class="flex items-center gap-2">
										<span>/</span><span>{{ lessonPlan.coCreatorName }}</span>
									</div>
								</div>
							</div>
						</div>
						<div class="flex gap-5 items-center justify-end w-24 float-right print:hidden">
							<button v-if="withEditButton"
								type="button"
								class="btn btn-primary relative btn-circle btn-md z-20 hover:scale-110"
								@click="$emit('editLessonPlan', lessonPlan)"
							>
								<PencilIcon class="w-5 h-5"></PencilIcon>
							</button>
							<div v-if="withCollectionSelector">
								<!-- Add to collections modal -->
								<LessonPlanCollectionsSelector
									:lessonPlan="lessonPlan"
									:align="'end'"
									@changed="$emit('collectionsChanged', lessonPlan)"
								/>

								<!-- tooltip -->
								<template v-if="collectionTooltipMessage">
									<PopoverRoot :defaultOpen="true" @update:open="showCollectionTooltip = $event">
										<PopoverAnchor><div class="w-1 h-1"></div></PopoverAnchor>
										<PopoverPortal>
										<PopoverContent
											side="bottom"
											align="end"
											:side-offset="4"
											:collision-padding="16"
											:avoid-collisions="true"
											class="z-50 text-start text-sm font-semibold text-info-content bg-info w-64 p-3 border border-info-content/20 rounded-md shadow-md"
										>
											<div v-html="collectionTooltipMessage"></div>
											<PopoverArrow rounded class="fill-info stroke-info-content/20 -mt-px" />
										</PopoverContent>
										</PopoverPortal>
									</PopoverRoot>
								</template>
							</div>
						</div>
					</div>
				</div>

				<div class="flex flex-col gap-y-8 font-medium rounded">

					<!-- Supplies & Attachments -->
					<div class="grid grid-cols-2 gap-4">
						<div class="border-2 border-base-100 rounded-lg p-4">
							<div class="font-semibold mb-2 text-xs uppercase text-base-content-light">
								{{ $t("Supplies") }}
							</div>
							<ul v-if="lessonPlan?.supplies?.length > 0" class="list-disc pl-5 space-y-1">
								<li
									v-for="(item, idx) in showAllSupplies
										? lessonPlan.supplies
										: lessonPlan.supplies.slice(0, 3)"
									:key="item && item.id ? item.id : item + '_' + idx"
								>
									{{ item }}
								</li>
							</ul>
							<div v-if="lessonPlan?.supplies?.length > 3" class="mt-1">
								<button
									type="button"
									class="btn btn-sm btn-ghost"
									@click="showAllSupplies = !showAllSupplies"
								>
									<span v-if="!showAllSupplies" class="flex items-center gap-1 text-accent-focus lowercase">
										+<i18n-t keypath="{counter} more" tag="span">
											<template #counter>
												<span>{{ lessonPlan.supplies.length - 3 }}</span>
											</template>
										</i18n-t>
										<ChevronDownIcon class="w-4 h-4" />
									</span>
									<span v-else class="flex items-center gap-2">
										<span class="text-accent-focus lowercase">{{ $t("Show less") }}</span>
										<ChevronUpIcon class="w-4 h-4 text-accent-focus" />
									</span>
								</button>
							</div>
							<div v-else-if="!lessonPlan?.supplies?.length" class="text-sm italic text-base-content-light">
								{{ $t("None") }}
							</div>
						</div>
						<div class="border-2 border-base-100 rounded-lg p-4">
							<div class="font-semibold mb-2 text-xs uppercase text-base-content-light">
								{{ $t("Attachments") }}
							</div>
							<Attachments
								v-if="lessonPlan?.attachments?.length > 0"
								:key="showAllAttachments ? 'all' : 'limited'"
								:modelValue="visibleAttachments"
								:preview="false"
								:header="false"
								:externalFilesButton="false"
								:readOnly="true"
							/>
							<div v-if="lessonPlan?.attachments?.length > 3" class="mt-1">
								<button
									type="button"
									class="btn btn-sm btn-ghost"
									@click="showAllAttachments = !showAllAttachments"
								>
									<span v-if="!showAllAttachments" class="flex items-center gap-1 text-accent-focus lowercase">
										+<i18n-t keypath="{counter} more" tag="span">
											<template #counter>
												<span>{{ lessonPlan.attachments.length - 3 }}</span>
											</template>
										</i18n-t>
										<ChevronDownIcon class="w-4 h-4" />
									</span>
									<span v-else class="flex items-center gap-2">
										<span class="text-accent-focus lowercase">{{ $t("Show less") }}</span>
										<ChevronUpIcon class="w-4 h-4 text-accent-focus" />
									</span>
								</button>
							</div>
							<div v-else-if="!lessonPlan?.attachments?.length" class="text-sm italic text-base-content-light">
								{{ $t("None") }}
							</div>
						</div>
					</div>

					<!-- Description -->
					<TextEditor
						v-if="lessonPlan"
						:modelValue="lessonPlan.instructions"
						:readOnly="true"
					/>

					<!-- Goals -->
					<div class="px-4">
						<div class="font-semibold mb-4 text-xs uppercase text-base-content-light">
							{{ $tc("Goal", 2) }}
						</div>
						<GoalsDisplay
							v-if="lessonPlan.goals?.length"
							:goals="lessonPlan.goals"
							:curriculum="organisationsStore.currentOrganisation?.curriculum ?? null"
						/>
						<div v-else class="text-sm italic text-base-content-light">
							{{ $t("None") }}
						</div>
					</div>

					<!-- Gaan we nog nagaan of het nodig is of niet -->
					<!-- <div v-if="showOptionGuided" class="flex items-center gap-2">
							<input
								type="checkbox"
								class="checkbox checkbox-sm"
								v-model.lazy="lessonPlan.isGuided"
								name="guided"
								id="guided-activity"
								:disabled="readOnly"
							/>
							<label for="guided-activity" class="flex gap-2">
								<span class="text-sm mt-0.5">{{ $t("Under guidance of the teacher") }}</span>
								<LifebuoyIcon class="w-6 h-6" />
							</label>
						</div> -->
				</div>
			</div>

			<div class="flex absolute bottom-0 w-full bg-white/80 backdrop-blur-sm z-10 p-4 mt-6 border-t border-base-200 justify-between items-center">
				<div class="flex-shrink-0">
					<ActionConfirm
						position="top-start"
						class="w-[28rem]"
						v-slot="confirmationDialog"
						:message="$t('Are you sure?')"
						:explainer="$t('Deleting this lesson plan from your library will NOT remove it from any planned lessons.')"
					>
						<button
							v-if="withDeleteButton && lessonPlan?.id && isOwnLessonPlan"
							type="button"
							class="btn btn-outline btn-danger btn-square"
							:disabled="deleting"
							@click="confirmRemoveLessonPlan(confirmationDialog)"
						>
							<TrashIcon class="w-5 h-5" />
						</button>
						<button
							v-else-if="lessonPlan?.isSavedInLib && !isOwnLessonPlan && $router.getRootComponentName() === 'LessonPlanLibrary'"
							type="button"
							class="btn btn-outline btn-danger"
							:disabled="deleting"
							@click="confirmRemoveLessonPlan(confirmationDialog)"
						>
							<span v-show="deleting" class="loading loading-spinner"></span>
							{{ $t('Remove from My Library') }}
						</button>
					</ActionConfirm>
				</div>

				<div class="flex gap-3 items-center">
					<template v-if="withSelectButtons">
						<ActionConfirm
							position="top-end"
							class="w-[34rem]"
							v-slot="confirmationDialog"
							:message="$t('Please respect the author’s work when using the lesson plan as a template for a new lesson plan.')"
							:explainer="$t('You can use this lesson plan as a template for your own lesson plan, but you’re not allowed to distribute it further outside your organisation. Also, check in the description of the lesson plan if any additional copyrights apply.')"
							:confirm-label="$t('OK')"
							:cancel-label="$t('Cancel')"
							button-orientation="vertical"
							confirm-style="primary"
						>
							<button type="button" class="btn btn-ghost" @click="confirmCreateOwnVersion(confirmationDialog)" :disabled="deleting">
								{{ isOwnLessonPlan ? $t("Create new version") : $t("Create own version") }}
							</button>
						</ActionConfirm>

						<button class="btn btn-primary min-w-28" @click="selectOnly" :disabled="saving || isAlreadyInBlock">
							<span v-if="saving" class="loading loading-spinner"></span>
							{{ $t("Select") }}
						</button>
					</template>

					<template v-else-if="withReplaceButton">
						<ActionConfirm
							position="top-end"
							class="w-[34rem]"
							v-slot="confirmationDialog"
							:message="$t('Please respect the author’s work when using the lesson plan as a template for a new lesson plan.')"
							:explainer="$t('You can use this lesson plan as a template for your own lesson plan, but you’re not allowed to distribute it further outside your organisation. Also, check in the description of the lesson plan if any additional copyrights apply.')"
							:confirm-label="$t('OK')"
							:cancel-label="$t('Cancel')"
							button-orientation="vertical"
							confirm-style="primary"
						>
							<button type="button" class="btn btn-ghost" @click="confirmCreateOwnVersion(confirmationDialog)" :disabled="deleting">
								{{ $t("Replace with own version") }}
							</button>
						</ActionConfirm>

						<div v-if="withAddToLibraryButton && initiallyNotInLib" class="flex gap-2.5">
							<button type="button" class="btn btn-ghost" @click="animatedClose" :disabled="deleting">
								{{ $t("Close") }}
							</button>
							<button class="btn btn-primary" @click="addToLibrary" :disabled="saving || deleting || lessonPlan.isSavedInLib">
								<span v-show="saving" class="loading loading-spinner"></span>
								{{ $t("Add to Library") }}
							</button>
						</div>

						<button v-else type="button" class="btn btn-ghost" @click="animatedClose" :disabled="deleting">
							{{ $t("Close") }}
						</button>
					</template>
					<template v-else>
						<button type="button" class="btn btn-ghost" @click="animatedClose" :disabled="deleting">
							{{ $t("Close") }}
						</button>
					</template>
				</div>
			</div>
		</template>
	</Drawer>
</template>

<script lang="ts">
import Drawer from "@/components/ui/Drawer.v2.vue";
import ActionConfirm from "@/components/ui/ActionConfirm.vue";
import { TrashIcon, PencilIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/outline";
import { PopoverAnchor, PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot } from 'reka-ui';
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import { mapStores } from "pinia";
import LessonPlanCollectionsSelector from "@/components/LessonPlanCollectionsSelector.vue";
import GoalsDisplay from "@/components/GoalsDisplay.vue";
import Attachments from "@/components/Attachments.vue";
import { useUsersStore } from "@/stores/Users.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { LessonPlan } from "@/models/LessonPlan.model";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import { tiptapUtils } from '@/utils/TipTap.ts';
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import TextEditor from '@/components/ui/TextEditor.vue';

export default {
	emits: ["requestUnmount", "selectLessonPlan", "addToLibrary", "editLessonPlan", "collectionsChanged", "createOwnVersion", "removedFromLibrary"],

	props: {
		lessonPlan: {
			type: Object as () => LessonPlan,
			default: null,
		},
		initialWidth: {
			type: String,
			default: '3xl',
		},
		withSelectButtons: {
			type: Boolean,
			default: false,
		},
		withEditButton: {
			type: Boolean,
			default: false,
		},
		withAddToLibraryButton: {
			type: Boolean,
			default: false,
		},
		withDeleteButton: {
			type: Boolean,
			default: false,
		},
		withReplaceButton: {
			type: Boolean,
			default: false,
		},
		skipOpenAnimation: {
			type: Boolean,
			default: false,
		},
		withCollectionSelector: {
			type: Boolean,
			default: false,
		},
		collectionTooltipMessage: {
			type: String,
			default: '',
		},
	},

	components: {
		Drawer,
		ActionConfirm,
		TrashIcon,
		PencilIcon,
		ChevronDownIcon,
		ChevronUpIcon,
		LessonPlanCollectionsSelector,
		GoalsDisplay,
		Attachments,
		PopoverAnchor,
		PopoverArrow,
		PopoverContent,
		PopoverPortal,
		PopoverRoot,
		TextEditor,
	},

	data() {
		return {
			slotHide: null as any,
			deleting: false,
			saving: false,
			showAllSupplies: false,
			showAllAttachments: false,
			showCollectionTooltip: false,
			// justAddedToLibrary: false,
			initiallyNotInLib: false,
		}
	},

	created() {
		this.initiallyNotInLib = !this.lessonPlan?.isSavedInLib;
	},

	watch: {
		'lessonPlan.isSavedInLib'(newVal: boolean) {
			if (newVal) {
				// this.justAddedToLibrary = true;
				this.saving = false;
			}
		},
	},

	computed: {
		...mapStores(useLessonPlanCollectionsStore, useLessonPlansStore, useUsersStore, useDiscoverStateStore, useOrganisationsStore),

		isOwnLessonPlan(): boolean {
			if(this.lessonPlan && this.usersStore.me) {
				return this.lessonPlan.isMine(this.usersStore.me);
			}
			return false;
		},

		visibleAttachments() {
			if (!this.lessonPlan?.attachments?.length) return [];
			const sorted = [...this.lessonPlan.attachments].sort((a: any, b: any) =>
				b.created_at.localeCompare(a.created_at)
			);
			return this.showAllAttachments ? sorted : sorted.slice(0, 3);
		},

		isAlreadyInBlock(): boolean {
			if (!this.lessonPlan?.id) return false;
			return this.discoverStateStore.lessonPlanIdsInBlock.includes(this.lessonPlan.id);
		},

		// similar function exists on LessonActivities component - keep them in sync
		showCreator(): boolean {
			// show creator info if it's not your own lesson plan...
			if (!this.isOwnLessonPlan) {
				return true;
			// ...or if it's your own but you are not the original creator
			} else if( this.lessonPlan.originalCreator?.id && this.lessonPlan.originalCreator.id !== this.usersStore.me?.id) {
				return true;
			}
			return false;
		}
	},

	methods: {
		instructionsToHtml(instructions: string) {
			if(!instructions) return '';
			instructions = instructions.trim();
			// Check if the string looks like a valid json object (meaning it's tiptap generated content)
			// If it fails, meaning it's most likely a string from an older lesson plan, return the original string (after escaping it to prevent XSS)
			try {
				const instructionsAsObject = JSON.parse(instructions);
				return tiptapUtils.jsonToHtml(instructionsAsObject);
			} catch (e) {
				const div = document.createElement('div');
				div.textContent = instructions;
				return div.innerHTML.replace(/\n/g, '<br>'); // preserve line breaks
			}
		},

		setSlotHide(hideFn: any) {
			if (!this.slotHide) this.slotHide = hideFn;
			return;
		},

		hide() {
			this.$emit("requestUnmount");
		},

		animatedClose() {
			this.slotHide ? this.slotHide() : this.hide();
		},

		async confirmCreateOwnVersion(confirmationDialog: any) {
			// If it's not your own lesson plan, first show a confirmation dialog with a copyright reminder
			if (!this.isOwnLessonPlan) {
				const confirmed = await confirmationDialog.show();
				if (!confirmed) return;
			}

			this.$emit("createOwnVersion", this.lessonPlan);
			this.animatedClose();
		},

		selectOnly() {
			this.$emit("selectLessonPlan", this.lessonPlan);
			this.animatedClose();
		},

		addToLibrary() {
			this.saving = true;
			this.$emit('addToLibrary', this.lessonPlan);
		},

		async confirmRemoveLessonPlan(confirmationDialog: any) {
			const confirmed = await confirmationDialog.show();
			if (!confirmed) return;

			if (this.deleting) return;
			this.deleting = true;

			try {
				// Remove from all collections first
				if (this.lessonPlan.collectionIds?.length) {
					await Promise.all(
						this.lessonPlan.collectionIds.map(async (collectionId: string) => {
							const collection = this.lessonPlanCollectionsStore.find(collectionId) as LessonPlanCollection;
							if (collection) {
								try {
									await this.lessonPlanCollectionsStore.removeLessonPlanFromCollection(collection, this.lessonPlan);
								} catch (err) {
									console.warn('Failed removing lesson plan from collection', collectionId, err);
								}
							}
						})
					);
				}

				await this.lessonPlansStore.delete(this.lessonPlan);

				// Update UI based on ownership
				if (this.isOwnLessonPlan) {
					this.hide();
				} else {
					// If it's an creator's plan, notify and emit so the Parent flips the flag
					this.$notify({ text: this.$t("Lesson plan removed from library"), type: "success" });

					this.$emit('removedFromLibrary', this.lessonPlan);

					// Brief delay for the user
					setTimeout(() => {
						this.deleting = false;
						this.animatedClose();
					}, 500);
				}
			} catch (e: any) {
				this.deleting = false;
				if (!(e instanceof ApiErrorMessageBag)) throw e;
			}
		},
	},
};
</script>
